"use client";

import { motion } from "motion/react";
import { ReactNode } from "react";
import { cn } from "@/lib/utils";

type Pillar = { title: string; body: string; icon?: ReactNode; metric?: string; metricLabel?: string; accent?: "red" | "coral" | "purple" | "cyan" | "denim" | "emerald" | "lime" | "amber-gold" | "crimson" };

const ACCENT: Record<NonNullable<Pillar["accent"]>, { bg: string; text: string; border: string; ring: string }> = {
  red: { bg: "from-red-500 to-coral-500", text: "text-red-400", border: "border-red-500/30", ring: "ring-red-500/30" },
  coral: { bg: "from-coral-500 to-amber-gold-500", text: "text-coral-400", border: "border-coral-500/30", ring: "ring-coral-500/30" },
  purple: { bg: "from-purple-500 to-cyan-500", text: "text-purple-400", border: "border-purple-500/30", ring: "ring-purple-500/30" },
  cyan: { bg: "from-cyan-500 to-emerald-500", text: "text-cyan-400", border: "border-cyan-500/30", ring: "ring-cyan-500/30" },
  denim: { bg: "from-denim-500 to-purple-500", text: "text-denim-400", border: "border-denim-500/30", ring: "ring-denim-500/30" },
  emerald: { bg: "from-emerald-500 to-lime-500", text: "text-emerald-400", border: "border-emerald-500/30", ring: "ring-emerald-500/30" },
  lime: { bg: "from-lime-500 to-amber-gold-500", text: "text-lime-400", border: "border-lime-500/30", ring: "ring-lime-500/30" },
  "amber-gold": { bg: "from-amber-gold-500 to-coral-500", text: "text-amber-gold-400", border: "border-amber-gold-500/30", ring: "ring-amber-gold-500/30" },
  crimson: { bg: "from-crimson-500 to-red-500", text: "text-crimson-400", border: "border-crimson-500/30", ring: "ring-crimson-500/30" },
};

const ACCENT_BG: Record<NonNullable<Pillar["accent"]>, string> = {
  red: "from-red-500/10 to-red-500/0",
  coral: "from-coral-500/10 to-coral-500/0",
  purple: "from-purple-500/10 to-purple-500/0",
  cyan: "from-cyan-500/10 to-cyan-500/0",
  denim: "from-denim-500/10 to-denim-500/0",
  emerald: "from-emerald-500/10 to-emerald-500/0",
  lime: "from-lime-500/10 to-lime-500/0",
  "amber-gold": "from-amber-gold-500/10 to-amber-gold-500/0",
  crimson: "from-crimson-500/10 to-crimson-500/0",
};

export function PillarGridBlock({ pillars, columns = 3, variant = "card", title, eyebrow, description }: { pillars: Pillar[]; columns?: 2 | 3 | 4 | 5; variant?: "card" | "bordered" | "metric" | "numbered"; title?: string; eyebrow?: string; description?: string }) {
  const colClass = {
    2: "grid-cols-1 sm:grid-cols-2",
    3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
    5: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5",
  }[columns];

  return (
    <div className="space-y-10">
      {(eyebrow || title || description) && (
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mx-auto max-w-3xl text-center">
          {eyebrow && <div className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-amber-gold-500">{eyebrow}</div>}
          {title && <h2 className="font-rajdhani text-3xl font-bold text-foreground lg:text-4xl">{title}</h2>}
          {description && <p className="mt-3 text-foreground/80/80">{description}</p>}
        </motion.div>
      )}
      <div className={cn("grid gap-6", colClass)}>
        {pillars.map((p, i) => {
          const accent = p.accent ?? "purple";
          const a = ACCENT[accent];
          const ab = ACCENT_BG[accent];
          return (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.06 }} className={cn("glass-card relative overflow-hidden rounded-2xl border p-6", variant === "bordered" && cn("border-2", a.border), variant === "card" && "border-border")}>
              <div className={cn("absolute inset-0 bg-gradient-to-br", ab)} />
              <div className="relative">
                {variant === "numbered" && <div className={cn("mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-muted font-rajdhani text-lg font-bold ring-2", a.ring, a.text)}>{String(i + 1).padStart(2, "0")}</div>}
                {p.icon && <div className={cn("mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-muted", a.text)}>{p.icon}</div>}
                {p.metric && <div className={cn("mb-2 font-rajdhani text-4xl font-bold", a.text)}>{p.metric}</div>}
                <h3 className="font-rajdhani text-xl font-bold text-foreground">{p.title}</h3>
                {p.metricLabel && <div className="mt-1 text-xs uppercase tracking-wider text-muted-foreground">{p.metricLabel}</div>}
                <p className="mt-3 text-sm leading-relaxed text-foreground/80/85">{p.body}</p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
